export type NegotiationNotice =
  | { type: "minor_candidate"; positions: number[] }
  | { type: "minor_active"; positions: number[]; totalCents: number }
  | { type: "adult_only" }
  | { type: "balance_shortfall"; shortfallCents: number };

export type NegotiationNotices = {
  cycleId: string;
  settlementMode: "prepaid" | "legacy";
  sealed: boolean;
  notices: NegotiationNotice[];
};

const amount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Refuse partial or duplicate notices: counts and amounts come from one server snapshot. */
export function parseNegotiationNotices(value: unknown): NegotiationNotices | null {
  if (!value || typeof value !== "object") return null;
  const body = value as Record<string, unknown>;
  if (
    typeof body.cycleId !== "string" ||
    !body.cycleId ||
    (body.settlementMode !== "prepaid" && body.settlementMode !== "legacy") ||
    !Array.isArray(body.notices) ||
    body.notices.length > 4
  )
    return null;
  if (body.sealed !== undefined && typeof body.sealed !== "boolean") return null;
  const notices: NegotiationNotice[] = [];
  const types = new Set<string>();
  for (const candidate of body.notices) {
    if (!candidate || typeof candidate !== "object") return null;
    const item = candidate as Record<string, unknown>;
    if (typeof item.type !== "string" || types.has(item.type)) return null;
    types.add(item.type);
    if (item.type === "adult_only") notices.push({ type: item.type });
    else if (item.type === "balance_shortfall") {
      if (!amount(item.shortfallCents) || item.shortfallCents === 0) return null;
      notices.push({ type: item.type, shortfallCents: item.shortfallCents });
    } else if (item.type === "minor_candidate" || item.type === "minor_active") {
      if (
        !Array.isArray(item.positions) ||
        item.positions.length === 0 ||
        item.positions.length > 10_000 ||
        item.positions.some((position) => !amount(position) || position === 0) ||
        new Set(item.positions).size !== item.positions.length
      )
        return null;
      const positions = [...item.positions].sort((a, b) => a - b);
      if (item.type === "minor_candidate") notices.push({ type: item.type, positions });
      else {
        if (!amount(item.totalCents)) return null;
        notices.push({ type: item.type, positions, totalCents: item.totalCents });
      }
    } else return null;
  }
  return {
    cycleId: body.cycleId,
    settlementMode: body.settlementMode,
    sealed: body.sealed === true,
    notices,
  };
}
